import React, { useState, useEffect, useMemo } from 'react';
import { UserContext } from '../user-provider/user-context';
import { NotificationsContext } from './notifications-context';
import { mutate } from 'swr';
import useSWRSubscription from 'swr/subscription';
import { useI18n } from '@locales/client';
import Cookies from 'js-cookie';
import { useToast } from '@/shared/providers/toast-provider/ToastProvider';
import { usePageVisitData } from '@/shared/hooks';

const INIT_DATA = {
  balance: null,
  transaction: null,
  modal: null,
};

const NotificationsProvider = ({ children }) => {
  const [notifications, setNotifications] = useState(INIT_DATA);
  const { user } = React.useContext(UserContext);
  const [isNotifyLoading, setIsNotifyLoading] = useState(user?.isAuth);
  const { showToast } = useToast();
  const t = useI18n();
  const { getPattern } = usePageVisitData();

  const { data: message, error } = useSWRSubscription(
    user.isAuth ? 'notifications' : null,
    (key, { next }) => {
      const wsUrl =
        process.env.NODE_ENV === 'development'
          ? `${process.env.NEXT_PUBLIC_WEB_SOCKET_URL}/notify`
          : `wss://${window.location.host}/server/notify`;

      const socket = new WebSocket(wsUrl);

      socket.onopen = () => {
        const pattern = getPattern();
        socket.send(pattern);
        setIsNotifyLoading(false);
      };

      socket.onmessage = (event) => {
        next(null, event.data);
      };

      socket.onerror = (error) => {
        next(error);
      };

      socket.onclose = () => {};

      return () => {
        socket.close();
      };
    }
  );

  useEffect(() => {
    if (message) {
      try {
        const messageData = JSON.parse(message);
        if (Array.isArray(messageData)) {
          const [type, value] = messageData;

          setNotifications((prev) => ({
            ...prev,
            [type]: value,
          }));

          if (type === 'notify') {
            showToast(value);
            if (value.save) {
              mutate('/notifications/list');
            }
            if (value.type === 'bonus') {
              mutate('/profile/getBonuses');
              Cookies.set('bonusRecieved', true, { expires: 365 });
            }
          }
        }
      } catch (e) {
        console.error('Failed to parse message data:', e);
      }
    }
  }, [message, showToast]);

  useEffect(() => {
    if (error) {
      console.error('WebSocket error:', error);
      setIsNotifyLoading(false);
    }
  }, [error]);

  const notificationsValue = useMemo(
    () => ({
      notifications,
      setNotifications,
      isNotifyLoading,
    }),
    [notifications, isNotifyLoading]
  );

  return (
    <NotificationsContext.Provider value={notificationsValue}>
      {children}
    </NotificationsContext.Provider>
  );
};

export { NotificationsProvider };
